import axios from 'axios';
import { API } from '../constants/api';

const STORAGE_KEY = 'pendingExamUploadCleanup';
const CLEANUP_ATTEMPTS_STORAGE_KEY = 'pendingExamUploadCleanupAttempts';
const MAX_PENDING_EXAM_UPLOAD_AGE_MS = 24 * 60 * 60 * 1000;

const canUseStorage = () => typeof window !== 'undefined' && !!window.localStorage;
const canUseSessionStorage = () => typeof window !== 'undefined' && !!window.sessionStorage;

const isValidRecordAge = (createdAt) => {
  if (!Number.isFinite(createdAt)) {
    return false;
  }

  return Date.now() - createdAt <= MAX_PENDING_EXAM_UPLOAD_AGE_MS;
};

const sanitizePendingExamUploadRecord = (record) => {
  if (!record || !record.exam || !record.exam.id) {
    return null;
  }

  if (!isValidRecordAge(record.createdAt)) {
    return null;
  }

  return {
    ...record,
    cleanupRequired: !!record.cleanupRequired,
    createdAt: record.createdAt,
  };
};

const readCleanupAttempts = () => {
  if (!canUseSessionStorage()) {
    return {};
  }

  try {
    const rawValue = window.sessionStorage.getItem(CLEANUP_ATTEMPTS_STORAGE_KEY);

    if (!rawValue) {
      return {};
    }

    const parsedValue = JSON.parse(rawValue);
    return parsedValue && typeof parsedValue === 'object' ? parsedValue : {};
  } catch (error) {
    return {};
  }
};

const writeCleanupAttempts = (cleanupAttempts) => {
  if (!canUseSessionStorage()) {
    return;
  }

  if (!cleanupAttempts || Object.keys(cleanupAttempts).length === 0) {
    window.sessionStorage.removeItem(CLEANUP_ATTEMPTS_STORAGE_KEY);
    return;
  }

  window.sessionStorage.setItem(CLEANUP_ATTEMPTS_STORAGE_KEY, JSON.stringify(cleanupAttempts));
};

const getCleanupAttemptKey = (examId, userId = null) => `${userId ?? 'all'}:${examId}`;

const hasCleanupAttemptedThisSession = (examId, userId = null) => {
  if (!examId) {
    return false;
  }

  return !!readCleanupAttempts()[getCleanupAttemptKey(examId, userId)];
};

const markCleanupAttemptedThisSession = (examId, userId = null) => {
  if (!examId) {
    return;
  }

  writeCleanupAttempts({
    ...readCleanupAttempts(),
    [getCleanupAttemptKey(examId, userId)]: Date.now(),
  });
};

const clearCleanupAttemptsForExamIds = (examIds, userId = null) => {
  if (!Array.isArray(examIds) || examIds.length === 0) {
    return;
  }

  const currentAttempts = readCleanupAttempts();
  let hasChanges = false;

  examIds.forEach((examId) => {
    const attemptKey = getCleanupAttemptKey(examId, userId);

    if (currentAttempts[attemptKey]) {
      delete currentAttempts[attemptKey];
      hasChanges = true;
    }
  });

  if (hasChanges) {
    writeCleanupAttempts(currentAttempts);
  }
};

const readPendingExamUploads = () => {
  if (!canUseStorage()) {
    return [];
  }

  try {
    const rawValue = window.localStorage.getItem(STORAGE_KEY);
    if (!rawValue) {
      return [];
    }

    const parsedValue = JSON.parse(rawValue);

    if (!Array.isArray(parsedValue)) {
      return [];
    }

    const sanitizedValue = parsedValue
      .map((record) => sanitizePendingExamUploadRecord(record))
      .filter(Boolean);

    if (sanitizedValue.length !== parsedValue.length) {
      writePendingExamUploads(sanitizedValue);
    }

    return sanitizedValue;
  } catch (error) {
    return [];
  }
};

const writePendingExamUploads = (pendingExamUploads) => {
  if (!canUseStorage()) {
    return;
  }

  if (!Array.isArray(pendingExamUploads) || pendingExamUploads.length === 0) {
    window.localStorage.removeItem(STORAGE_KEY);
    return;
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(pendingExamUploads));
};

const normalizeExamRecord = (exam, userId) => {
  if (!exam || !exam.id) {
    return null;
  }

  return {
    exam: {
      id: exam.id,
      id_academia: exam.id_academia,
      nombre: exam.nombre,
      visible: exam.visible,
    },
    userId: userId || null,
    cleanupRequired: false,
    createdAt: Date.now(),
  };
};

const buildCleanupHeaders = (token) => {
  const headers = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

const matchUser = (record, userId) => {
  if (userId === null || typeof userId === 'undefined') {
    return true;
  }

  return `${record.userId}` === `${userId}`;
};

export const getPendingExamUploads = (userId = null, options = {}) => {
  const { cleanupRequiredOnly = false } = options;

  return readPendingExamUploads().filter((record) => {
    if (!matchUser(record, userId)) {
      return false;
    }

    if (cleanupRequiredOnly && !record.cleanupRequired) {
      return false;
    }

    if (cleanupRequiredOnly && hasCleanupAttemptedThisSession(record.exam?.id, record.userId)) {
      return false;
    }

    return true;
  });
};

export const hasPendingExamUploads = (userId = null, options = {}) => {
  return getPendingExamUploads(userId, options).length > 0;
};

export const registerPendingExamUpload = (exam, userId) => {
  const normalizedRecord = normalizeExamRecord(exam, userId);
  if (!normalizedRecord) {
    return;
  }

  clearCleanupAttemptsForExamIds([normalizedRecord.exam.id], normalizedRecord.userId);

  const currentRecords = readPendingExamUploads();
  const alreadyExists = currentRecords.some((record) => `${record.exam?.id}` === `${normalizedRecord.exam.id}`);

  if (alreadyExists) {
    writePendingExamUploads(
      currentRecords.map((record) => {
        if (`${record.exam?.id}` !== `${normalizedRecord.exam.id}`) {
          return record;
        }

        return {
          ...record,
          exam: normalizedRecord.exam,
          userId: normalizedRecord.userId,
        };
      })
    );
    return;
  }

  writePendingExamUploads([...currentRecords, normalizedRecord]);
};

export const registerPendingExamUploads = (exams, userId) => {
  if (!Array.isArray(exams) || exams.length === 0) {
    return;
  }

  exams.forEach((exam) => registerPendingExamUpload(exam, userId));
};

export const unregisterPendingExamUpload = (examId, userId = null) => {
  if (!examId) {
    return;
  }

  clearCleanupAttemptsForExamIds([examId], userId);

  writePendingExamUploads(
    readPendingExamUploads().filter((record) => {
      if (`${record.exam?.id}` !== `${examId}`) {
        return true;
      }

      return !matchUser(record, userId);
    })
  );
};

export const unregisterPendingExamUploads = (examIds, userId = null) => {
  if (!Array.isArray(examIds) || examIds.length === 0) {
    return;
  }

  clearCleanupAttemptsForExamIds(examIds, userId);

  const examIdsMap = examIds.reduce((currentMap, examId) => {
    if (examId) {
      currentMap[`${examId}`] = true;
    }

    return currentMap;
  }, {});

  writePendingExamUploads(
    readPendingExamUploads().filter((record) => {
      if (!examIdsMap[`${record.exam?.id}`]) {
        return true;
      }

      return !matchUser(record, userId);
    })
  );
};

export const markPendingExamUploadsForCleanup = (examIds, userId = null) => {
  if (!Array.isArray(examIds) || examIds.length === 0) {
    return;
  }

  clearCleanupAttemptsForExamIds(examIds, userId);

  const examIdsMap = examIds.reduce((currentMap, examId) => {
    if (examId) {
      currentMap[`${examId}`] = true;
    }

    return currentMap;
  }, {});

  writePendingExamUploads(
    readPendingExamUploads().map((record) => {
      if (!examIdsMap[`${record.exam?.id}`] || !matchUser(record, userId)) {
        return record;
      }

      return {
        ...record,
        cleanupRequired: true,
      };
    })
  );
};

export const markAllPendingExamUploadsForCleanup = (userId = null) => {
  clearCleanupAttemptsForExamIds(
    readPendingExamUploads()
      .filter((record) => matchUser(record, userId))
      .map((record) => record.exam?.id)
      .filter(Boolean),
    userId,
  );

  writePendingExamUploads(
    readPendingExamUploads().map((record) => {
      if (!matchUser(record, userId)) {
        return record;
      }

      return {
        ...record,
        cleanupRequired: true,
      };
    })
  );
};

export const clearPendingExamUploadsCleanupRequirement = (examIds, userId = null) => {
  if (!Array.isArray(examIds) || examIds.length === 0) {
    return;
  }

  const examIdsMap = examIds.reduce((currentMap, examId) => {
    if (examId) {
      currentMap[`${examId}`] = true;
    }

    return currentMap;
  }, {});

  writePendingExamUploads(
    readPendingExamUploads().map((record) => {
      if (!examIdsMap[`${record.exam?.id}`] || !matchUser(record, userId)) {
        return record;
      }

      return {
        ...record,
        cleanupRequired: false,
      };
    })
  );
};

export const attemptKeepalivePendingExamUploadsCleanup = ({ userId = null, token = null } = {}) => {
  const pendingExamUploads = getPendingExamUploads(userId, { cleanupRequiredOnly: true });

  if (pendingExamUploads.length === 0 || typeof fetch === 'undefined') {
    return;
  }

  const headers = buildCleanupHeaders(token);

  pendingExamUploads.forEach((record) => {
    try {
      fetch(API.EXAMENES.EXAMENES, {
        method: 'DELETE',
        headers,
        body: JSON.stringify(record.exam),
        keepalive: true,
        credentials: 'same-origin',
      });
    } catch (error) {
      // Best effort cleanup on unload. Persistent cleanup runs again on next boot/reconnect.
    }
  });
};

export const cleanupPendingExamUploads = async ({ userId = null, token = null } = {}) => {
  const pendingExamUploads = getPendingExamUploads(userId, { cleanupRequiredOnly: true });
  const removedExamIds = [];
  const failedExamIds = [];
  const disabledCleanupExamIds = [];

  for (const record of pendingExamUploads) {
    markCleanupAttemptedThisSession(record.exam?.id, record.userId);

    try {
      await axios({
        method: 'DELETE',
        url: API.EXAMENES.EXAMENES,
        data: record.exam,
        headers: buildCleanupHeaders(token),
      });
      removedExamIds.push(record.exam.id);
    } catch (error) {
      const status = error?.response?.status;

      if (status === 400 || status === 404) {
        removedExamIds.push(record.exam.id);
      } else if (status && status !== 401 && status !== 498) {
        disabledCleanupExamIds.push(record.exam.id);
      } else {
        failedExamIds.push(record.exam.id);
      }
    }
  }

  if (removedExamIds.length > 0) {
    unregisterPendingExamUploads(removedExamIds, userId);
  }

  if (disabledCleanupExamIds.length > 0) {
    clearPendingExamUploadsCleanupRequirement(disabledCleanupExamIds, userId);
  }

  return {
    removedExamIds,
    failedExamIds,
    disabledCleanupExamIds,
  };
};